// src/components/charts/DistribucionIngresosChart.jsx
import React, { useState, useEffect } from 'react';
import { Card, Alert, Spinner } from 'react-bootstrap';
import kpiService from '../../services/kpiService';
import { formatCurrency } from '../../utils/formatters';

/**
 * Componente que muestra la distribución de ingresos por línea de negocio
 * Con barras horizontales personalizadas
 */
const DistribucionIngresosChart = ({ fechaInicio, fechaFin, empresaId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await kpiService.getDistribucionIngresos({
          fechaInicio,
          fechaFin,
          empresaId,
        });

        if (response.success && response.data) {
          setData(response.data);
        } else {
          throw new Error('No se pudieron cargar los datos');
        }
      } catch (err) {
        console.error('Error fetching distribución ingresos:', err);
        setError('Error al cargar el gráfico. Por favor, intenta nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fechaInicio, fechaFin, empresaId]);

  if (error) {
    return (
      <Card>
        <Card.Body>
          <Alert variant="danger">{error}</Alert>
        </Card.Body>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Cargando datos...</p>
        </Card.Body>
      </Card>
    );
  }

  // Validación adicional de datos
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Card>
        <Card.Body>
          <h5 className="mb-4">Distribución de ingresos vs proyectados por línea de negocio</h5>
          <p className="text-muted text-center py-4">No hay datos disponibles</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Body>
        <h5 className="mb-4">Distribución de ingresos vs proyectados por línea de negocio</h5>
        
        <div className="d-flex flex-column gap-4">
          {data.map((linea, idx) => {
            const real = linea.real || 0;
            const proyectado = linea.proyectado || 1; // Evitar división por 0
            const porcentajeReal = (real / proyectado) * 100;
            
            return (
              <div key={idx}>
                {/* Nombre de la línea */}
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-medium">{linea.linea || 'Sin nombre'}</span>
                  <div className="d-flex align-items-center gap-3">
                    <div className="text-end">
                      <small className="text-muted d-block">Real</small>
                      <span className="fw-semibold">{formatCurrency(real)}</span>
                    </div>
                    <div className="text-end">
                      <small className="text-muted d-block">Proyectado</small>
                      <span>{formatCurrency(proyectado)}</span>
                    </div>
                    <div className="text-end" style={{ minWidth: '60px' }}>
                      <small className="text-muted d-block">Desv.</small>
                      <span 
                        className="badge"
                        style={{ 
                          backgroundColor: (linea.desviacion || 0) >= 0 ? '#10b981' : '#ef4444' 
                        }}
                      >
                        {(linea.desviacion || 0) >= 0 ? '+' : ''}{linea.desviacion || 0}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Barra de progreso segmentada */}
                <div className="position-relative" style={{ height: '28px' }}>
                  {/* Barra de fondo (proyectado) */}
                  <div
                    className="position-absolute w-100 rounded"
                    style={{
                      height: '100%',
                      backgroundColor: '#e5e7eb',
                    }}
                  />
                  
                  {/* Barra de real */}
                  <div
                    className="position-absolute rounded"
                    style={{
                      height: '100%',
                      width: `${Math.min(porcentajeReal, 100)}%`,
                      backgroundColor: linea.color || '#3b82f6',
                      transition: 'width 0.6s ease-out',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Card.Body>
    </Card>
  );
};

export default DistribucionIngresosChart;